// api/shippingApi.js
// Client for the new provider-agnostic shipping endpoints
// (elakay_controllers/shipping.js / elakay_routes/shipping.js). Same
// fetch + auth-header + parseApiResponse conventions as deliveryApi.js;
// none of these responses are encrypted today, but parseApiResponse
// transparently handles either shape so this keeps working if that
// changes later.
import { elakay_API } from "../../config";
import { decryptData } from "./elakayCryptage";

const parseApiResponse = async (response) => {
  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || `HTTP ${response.status}`);
    error.code = data.code;
    throw error;
  }

  if (data?.ciphertext && data?.iv && data?.salt && data?.tag) {
    return decryptData(data);
  }

  return data;
};

const authHeaders = (token) => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`
});

// Sanitized provider status (no secrets) -- Family AND Seller dashboards
// both call this; `allowedProviders`/`sellerProvider` are what a seller's
// provider-selection UI should render from.
export const getShippingProviderStatus = async (token) => {
  const response = await fetch(`${elakay_API}/shipping/providers`, {
    headers: authHeaders(token)
  });
  return parseApiResponse(response);
};

export const getSellerShippingProvider = async (token, sellerId) => {
  const params = sellerId ? `?sellerId=${encodeURIComponent(sellerId)}` : "";
  const response = await fetch(`${elakay_API}/shipping/seller/provider${params}`, {
    headers: authHeaders(token)
  });
  return parseApiResponse(response);
};

export const updateSellerShippingProvider = async (token, provider, sellerId) => {
  const response = await fetch(`${elakay_API}/shipping/seller/provider`, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({ provider, ...(sellerId ? { sellerId } : {}) })
  });
  return parseApiResponse(response);
};

export const getOrderTracking = async (token, orderId, orderProductId) => {
  const response = await fetch(
    `${elakay_API}/shipping/tracking/order/${orderId}/${orderProductId}`,
    { headers: authHeaders(token) }
  );
  return parseApiResponse(response);
};

// Order Processing modal's shipping-mode selector
// (elakay_controllers/order.js#setOrderShippingMode). Distinct from
// updateSellerShippingProvider above: that one is the seller's DEFAULT
// provider (Delivery Center-wide); this one is the choice for ONE order
// item, which may follow that default ("auto") or override it.
export const updateOrderShippingMode = async (token, oid, opid, shippingMode) => {
  const response = await fetch(`${elakay_API}/order/${oid}/${opid}/shipping-mode`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify({ shippingMode })
  });
  return parseApiResponse(response);
};
